// Native OTA orchestration: the thin glue between the live `config/ota` manifest,
// the pure `selectOtaUpdate` decision (shared engine), and the Capgo plugin's
// native download/apply primitives. Everything here is a no-op off-native.
//
// Flow: read manifest -> decide (fail-closed) -> download zip natively -> queue
// it to apply on the next background (non-disruptive) or, if mandatory, apply +
// reload now. A freshly applied bundle that fails to boot is auto-rolled-back by
// the plugin (it never gets to call notifyAppReady within appReadyTimeout).
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { Capacitor } from '@capacitor/core';
import { selectOtaUpdate, type OtaConfig } from '@h2h/shared';
import { getAppVersion } from '../appVersion';

// Injected by Vite `define` at build time (see vite.config.ts). "dev" for a plain
// build; "<nativeBuild>.<otaCounter>" for a release bundle.
const BUILD_BUNDLE_VERSION: string =
  typeof __BUNDLE_VERSION__ === 'string' ? __BUNDLE_VERSION__ : 'dev';

const isDottedNumeric = (v: string): boolean => /^\d+(\.\d+)*$/.test(v);

// Session guards: don't run two checks at once, and don't re-download a target
// we've already fetched/queued this session (the manifest stays "newer" until the
// queued bundle actually activates on the next background).
let inFlight = false;
let handledVersion: string | null = null;

/**
 * Confirm to the plugin that this bundle booted OK. MUST run on every launch
 * (built-in or OTA); if a freshly applied bundle never reaches this within
 * `appReadyTimeout`, the plugin rolls back to the last good bundle. No-op on web.
 */
export async function markAppReady(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await CapacitorUpdater.notifyAppReady();
  } catch (err) {
    console.warn('[ota] notifyAppReady failed', err);
  }
}

/**
 * The web bundle version this install is effectively running. A real release
 * bundle stamps its own dotted version; the built-in store bundle (stamped "dev")
 * resolves to the floor "<nativeBuild>.0", which is what stops a stale manifest
 * from ever downgrading a fresh App Store build.
 */
function effectiveCurrentVersion(build: number): string {
  return isDottedNumeric(BUILD_BUNDLE_VERSION) ? BUILD_BUNDLE_VERSION : `${build}.0`;
}

/**
 * Check the live manifest and, if a strictly-newer compatible bundle is on offer,
 * download it and queue/apply it. Safe to call repeatedly (on launch, on resume,
 * whenever the manifest changes) — it dedupes and fails open.
 */
export async function runOtaCheck(cfg: OtaConfig | null | undefined): Promise<void> {
  if (!Capacitor.isNativePlatform() || inFlight) return;

  const info = await getAppVersion();
  const decision = selectOtaUpdate(cfg, {
    native: info.native,
    build: info.build,
    currentVersion: effectiveCurrentVersion(info.build)
  });
  if (!decision.apply || handledVersion === decision.version) return;

  inFlight = true;
  try {
    const bundle = await CapacitorUpdater.download({
      url: decision.url,
      version: decision.version,
      ...(decision.checksum ? { checksum: decision.checksum } : {})
    });
    // Mark handled only after a successful download so a failed fetch retries.
    handledVersion = decision.version;

    if (decision.mandatory) {
      // Replaces the active bundle and reloads immediately (destroys this JS
      // context — nothing after this runs).
      await CapacitorUpdater.set({ id: bundle.id });
    } else {
      // Non-disruptive: activates on the next background / relaunch.
      await CapacitorUpdater.next({ id: bundle.id });
    }
  } catch (err) {
    console.warn('[ota] update failed (will retry next launch)', err);
    handledVersion = null;
  } finally {
    inFlight = false;
  }
}
